/** Which window a screen opens on when the address names none (D-07, option A then C).
 *
 * Home opened on the declared window with the latest start date: the UK April year, labelled with one
 * company, while the figures summed three (G-Wviewer-01). KD decided on 2026-11-03: **first the next
 * filing's period**, the filing with the nearest due date that the person's role is asked about, and
 * **then the last choice this browser remembers**. Q-04 already made the window's label say what it
 * sums; this decides only which window is chosen when nobody chose one.
 *
 * Order of precedence, strongest first, and each is the whole answer when it applies:
 *
 * 1. The address names `from` and `to` (a link, a refresh, a choice just made). Not decided here:
 *    `resolveWindow` honours it, and so does nothing in this file.
 * 2. **The last window this person picked in this browser** (`lib/displayPreferences.ts`), when it is
 *    still one of the workspace's own windows. A person who chose a window has said what they want,
 *    and the next filing must not overrule them on every visit.
 * 3. **The next filing's period**, for a role that is asked about filings.
 * 4. The latest declared window (the old default), when none of the above gives one.
 *
 * Pure, so the rule is tested without a DOM. Nothing here knows a deadline: the dates and the status
 * are the server's (`GET /readiness/obligations`), and a filing whose law is not in force is never
 * the "next" one (the same rule Home's countdown keeps).
 */

import type { Obligation, Organization } from "../api/queries";
import { daysBetween } from "./readinessModel";

/** The roles that are asked about filings: those that submit, decide or issue one (`MAY_WRITE`,
 * `MAY_DECIDE` and `MAY_ISSUE` on the server). A Viewer or an Auditor reads; for them there is no
 * "next filing" to open on, so they get the remembered window or the latest. */
const ASKED_ABOUT_FILINGS: ReadonlySet<string> = new Set(["admin", "approver", "contributor"]);

export function isAskedAboutFilings(role: string | undefined): boolean {
  return role !== undefined && ASKED_ABOUT_FILINGS.has(role);
}

export interface Dates {
  period_start: string;
  period_end: string;
}

/** The period of the soonest filing still to do, as the dates of the window that holds it.
 *
 * "Owed" is `status === "required"` and determined; a filing whose law is not yet in force (a
 * proposed date, an enjoined regime) is not counted down to and is not chosen; a filing with no date
 * cannot be nearest. **The nearest date ahead wins; with none ahead, the most recently passed one**
 * (an overdue filing is still the next thing to look at, and the last one missed is nearer than one
 * missed long ago). A tie is broken by the regime key and the company's name, so the choice never
 * depends on the order the server happened to send.
 *
 * The filing is matched to its declared period by regime, company and year end, because the
 * obligation carries no period dates of its own. A filing with no matching period yields nothing:
 * guessing a window from a year end would put a screen on dates the workspace never declared.
 */
export function nextFilingWindow(
  obligations: readonly Obligation[] | undefined,
  organization: Organization | undefined,
  today: string,
): Dates | undefined {
  if (!obligations || !organization) return undefined;
  const candidates = obligations
    .filter(
      (obligation) =>
        obligation.status === "required" &&
        !obligation.undetermined &&
        !obligation.in_force_caveat &&
        !obligation.in_force_label &&
        obligation.due_date !== null,
    )
    .map((obligation) => ({
      obligation,
      days: daysBetween(today, obligation.due_date ?? ""),
      period: organization.periods.find(
        (period) =>
          period.regime_key === obligation.regime_key &&
          period.legal_entity_name === obligation.legal_entity_name &&
          period.end_date === obligation.financial_year_end,
      ),
    }))
    .filter(
      (candidate): candidate is typeof candidate & { days: number; period: NonNullable<typeof candidate.period> } =>
        candidate.days !== undefined && candidate.period !== undefined,
    );
  if (candidates.length === 0) return undefined;
  const ahead = candidates.filter((candidate) => candidate.days >= 0);
  const pool = ahead.length > 0 ? ahead : candidates;
  const order = (a: (typeof pool)[number], b: (typeof pool)[number]) =>
    ahead.length > 0 ? a.days - b.days : b.days - a.days;
  const [first] = [...pool].sort(
    (a, b) =>
      order(a, b) ||
      a.obligation.regime_key.localeCompare(b.obligation.regime_key) ||
      a.obligation.legal_entity_name.localeCompare(b.obligation.legal_entity_name),
  );
  return first ? { period_start: first.period.start_date, period_end: first.period.end_date } : undefined;
}

/** The window to open on, from the three sources in order. Each is `undefined` when it gives none; the
 * result is a member of `windows` or `undefined` (so the caller falls back to the latest). */
export function chooseDefault<W extends Dates>(
  windows: readonly W[],
  sources: { remembered?: Dates | undefined; next?: Dates | undefined },
): W | undefined {
  for (const wanted of [sources.remembered, sources.next]) {
    if (!wanted) continue;
    const found = windows.find(
      (window) => window.period_start === wanted.period_start && window.period_end === wanted.period_end,
    );
    if (found) return found;
  }
  return undefined;
}
